import {
  Box,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { CloseCircle, Refresh, Trash } from 'iconsax-react';
import type { FC } from 'react';

import { type UploadItem, UploadItemStatus } from '@core/types/file';
import { formatAttachmentBytes } from '@core/utils/emailAttachments';
import { hasValue } from '@core/utils/equality';

import { FileDropzone } from './FileDropzone';
import { FileTypeIcon } from './FileTypeIcon';
import type { FileUploadsManager } from './useFileUploads';

const STATUS_LABELS: Record<UploadItemStatus, string> = {
  [UploadItemStatus.Rejected]: 'Rejected',
  [UploadItemStatus.Uploading]: 'Uploading',
  [UploadItemStatus.Registering]: 'Saving',
  [UploadItemStatus.Done]: 'Uploaded',
  [UploadItemStatus.Failed]: 'Failed',
};

type UploadRowProps = {
  item: UploadItem;
  manager: FileUploadsManager;
};

const UploadRow: FC<UploadRowProps> = ({ item, manager }) => {
  const theme = useTheme();
  const isInProgress =
    item.status === UploadItemStatus.Uploading || item.status === UploadItemStatus.Registering;
  const isFailed = item.status === UploadItemStatus.Failed;
  const isRejected = item.status === UploadItemStatus.Rejected;
  const hasError = isFailed || isRejected;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: hasError ? theme.palette.SlabRed[600] : theme.palette.SlabGray[200],
        backgroundColor: hasError ? theme.palette.SlabRed[100] : 'transparent',
      }}
    >
      {hasError ? (
        <CloseCircle variant='TwoTone' color={theme.palette.SlabRed[600]} size={20} />
      ) : (
        <FileTypeIcon fileName={item.fileName} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body2' fontWeight={600} noWrap>
          {item.fileName}
        </Typography>
        {hasError && hasValue(item.errorMessage) ? (
          <Typography
            variant='secondary'
            sx={{ fontSize: '0.75rem', color: theme.palette.SlabRed[600] }}
          >
            {item.errorMessage}
          </Typography>
        ) : (
          <Typography variant='secondary' sx={{ fontSize: '0.75rem' }}>
            {formatAttachmentBytes(item.sizeBytes)} &middot; {STATUS_LABELS[item.status]}
          </Typography>
        )}
        {isInProgress && (
          <LinearProgress
            variant='determinate'
            value={item.progress}
            sx={{
              mt: 0.5,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.SlabGray[100],
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                backgroundColor: theme.palette.SlabIndigo[500],
              },
            }}
          />
        )}
      </Box>
      {isFailed && (
        <IconButton
          size='small'
          aria-label={`Retry ${item.fileName}`}
          onClick={() => manager.retry(item.id)}
        >
          <Refresh variant='TwoTone' color={theme.palette.SlabRed[600]} />
        </IconButton>
      )}
      <IconButton
        size='small'
        aria-label={`Remove ${item.fileName}`}
        onClick={() => manager.remove(item.id)}
      >
        <Trash
          variant='TwoTone'
          color={hasError ? theme.palette.SlabRed[600] : theme.palette.SlabIndigo[700]}
        />
      </IconButton>
    </Box>
  );
};

type Props = {
  manager: FileUploadsManager;
  canMarkSensitive?: boolean;
  isEmpty?: boolean;
};

export const FileUploadArea: FC<Props> = ({
  manager,
  canMarkSensitive = false,
  isEmpty = false,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {canMarkSensitive && (
        <FormControlLabel
          control={
            <Switch
              checked={manager.isSensitive}
              onChange={(event) => manager.setIsSensitive(event.target.checked)}
            />
          }
          label='Mark new uploads as sensitive'
        />
      )}
      <FileDropzone onFilesSelected={manager.addFiles} isEmpty={isEmpty} />
      {manager.items.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {manager.items.map((item) => (
            <UploadRow key={item.id} item={item} manager={manager} />
          ))}
        </Box>
      )}

      {manager.hasPendingUploads && (
        <Typography
          variant='secondary'
          sx={{ fontSize: '0.75rem', color: theme.palette.SlabGray[600] }}
        >
          Some files are still uploading. You can save now &mdash; they will finish uploading and
          attach automatically.
        </Typography>
      )}
    </Box>
  );
};
